<template>
    <div class="c-appointment-list">
        <SphereNavigation color="green" selected="planning" />

        <div class="inner-content">
            <div class="top-actions">
                <div>
                    <DatePicker v-model:date="date" :lockPast="false" color="green" v-if="!showUnplanned && searchText.length == 0"/>
                </div>

                <SearchField v-model:search="searchText" />
            </div>

            <div class="list-container">
                <div class="list">
                    <div class="list--item green" v-for="appointment in appointmentList" :key="appointment.id"
                        :class="{selected : selectedAppointment != null && selectedAppointment.id == appointment.id, active : isMeasurementActive(appointment)}"
                        @click.prevent="setSelectedAppointment(appointment)">
                        <div class="list--item--btns">
                            <AppointmentItemSales :appointment="appointment" />

                            <router-link :to="{ name: 'appointment-schedule', params: { id: appointment.id }}" v-if="!appointment.measurement_done">
                                <span v-if="isValidAppointmentDate(appointment.date)">{{formatAppointmentTime(appointment)}}</span>
                                <span v-else>{{$t('appointment_list.plan')}}</span>
                            </router-link>
                            <button class="btn-round black btn-result" v-if="appointment.measurement_done" @click.prevent="viewResult(appointment)">
                                <img src="/img/icon-view.svg" />
                            </button>

                            <ClientNames :appointment="appointment"/>
                        </div>

                        <div class="list--item--btns">
                            <button class="btn-round black" @click.prevent="clickEditAppointment(appointment)">
                                <img src="/img/icon-edit.svg" />
                            </button>

                            <button class="btn-round black" @click.prevent="deleteAppointment(appointment)">
                                <img src="/img/icon-delete-white.svg" />
                            </button>

                            <button class="btn-round black" @click.prevent="setSelectedAppointment(appointment)">
                                <img src="/img/icon-next.svg" v-if="!isMeasurementActive(appointment)" />
                                <img src="/img/icon-stop-red.svg" v-else />
                            </button>
                        </div>
                    </div>
                    <div v-if="appointmentList.length == 0" class="no-appointments">
                        {{$t('appointment_list.no_appointments')}}
                    </div>
                </div>

                <div class="detail-panel" v-if="selectedAppointment != null">
                    <MeasurementPanel :appointment="selectedAppointment" @close="closeMeasurementPanel"/>
                </div>
            </div>

            <div class="action-btns">
                <button class="btn-basic btn-toggle btn-toggle-appointments"
                    :class="{bordered : !showUnplanned, blue: showUnplanned}"
                    @click.prevent="toggleUnplanned">
                    <span v-if="!showUnplanned">
                        {{$t('appointment_list.btn_show_unplanned')}}
                    </span>
                    <span v-else>
                        {{$t('appointment_list.btn_show_all')}}
                    </span>
                </button>
            </div>
        </div>

        <ConfirmPopup v-if="appointmentToDelete != null"
            :title="$t('appointment_list.confirm_delete')"
            color="green"
            @cancel="cancelDeletePopup"
            @confirm="clickConfirmDelete"
         />

        <Loading v-if="loading" />
    </div>
</template>

<script>
import AppointmentItemSales from "@/components/AppointmentItemSales.vue";
import ClientNames from "@/components/ClientNames.vue";
import ConfirmPopup from "@/components/app/ConfirmPopup.vue";
import DatePicker from "@/components/DatePicker.vue";
import Loading from "@/components/app/Loading.vue";
import MeasurementPanel from "@/components/measurements/MeasurementPanel.vue";
import SphereNavigation from "@/components/app/SphereNavigation.vue";
import SearchField from "@/components/app/SearchField.vue";
import AppointmentListMixin from "@/mixins/AppointmentListMixin";
import CoconMixin from "@/mixins/CoconMixin";
import {newDateFromString, formatTime, isValidDate} from "@/utils/utils";
import {apiStore} from "@/stores/api.js";
import {socketStore} from "@/stores/socket";
import {measurementStore} from "@/stores/measurement";

export default {
    name: "AppointmentList",
    components : {
        AppointmentItemSales,
        ClientNames,
        ConfirmPopup,
        DatePicker,
        Loading,
        SphereNavigation,
        SearchField,
        MeasurementPanel
    },
    mixins: [AppointmentListMixin, CoconMixin],

    data() {
        return {
            appointmentsUnplanned : [],
            selectedAppointment : null,
            searchText : "",
            showUnplanned : false,
            appointmentToDelete : null
        };
    },

    async created() {
        await this.getUnplannedAppointments();
        document.addEventListener("visibilitychange", this.handleVisibilityChange);

        measurementStore().on("APPOINTMENT_UPDATED", this.onAppointmentUpdated);
    },

    unmounted() {
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);

        measurementStore().off("APPOINTMENT_UPDATED", this.onAppointmentUpdated);
    },

    computed: {
        appointmentList() {
            return this.showUnplanned ? this.appointmentsUnplanned : this.appointments.filter(x => !x.measurement_done);
        }
    },

    watch: {
        searchText() {
            if (this.searchText.trim() !== "") {
                this.appointments = apiStore().searchAppointments(this.searchText);

            } else {
                this.getAppointmentsForDate();
            }
        },

        date() {
            this.searchText = "";
            this.selectedAppointment = null;
        },

        appointments() {
            this.selectedAppointment = null;
        }
    },

    methods: {
        async getUnplannedAppointments() {
            this.appointmentsUnplanned = await apiStore().getAppointments("none");
        },

        setSelectedAppointment(appointment) {
            this.selectedAppointment = appointment;
        },

        closeMeasurementPanel() {
            this.selectedAppointment = null;
        },

        clickEditAppointment(appointment) {
            if (apiStore().shop && apiStore().shop.show_service_type === true) {
                this.$router.push({name : "appointment-type", params : {id : appointment.id, nextRoute: "appointment-edit"}});
            } else {
                this.$router.push({name : "appointment-edit", params : {id : appointment.id}});
            }
        },

        deleteAppointment(appointment) {
            this.appointmentToDelete = appointment;
        },

        cancelDeletePopup() {
            this.appointmentToDelete = null;
        },

        async clickConfirmDelete() {
            await apiStore().deleteAppointment(this.appointmentToDelete);
            this.getAppointmentsForDate();
            this.getUnplannedAppointments();

            this.appointmentToDelete = null;

            socketStore().emit("APPOINTMENT_UPDATED");
        },

        viewResult(appointment) {
            this.$router.push({name : "measurement-result", params : {appointmentid:appointment.id}});
        },

        async handleVisibilityChange() {
            if (!document.hidden) {
                await this.getAppointmentsForDate();
                await this.getUnplannedAppointments();
            }
        },

        toggleUnplanned() {
            this.showUnplanned = !this.showUnplanned;
            this.selectedAppointment = null;
        },

        onAppointmentUpdated() {
            setTimeout(() => {
                this.getAppointmentsForDate();
                this.getUnplannedAppointments();
            }, 500);
        },

        isValidAppointmentDate(dateString) {
            const date = newDateFromString(dateString);
            return isValidDate(date) && formatTime(date) !== "00:00";
        },

        appointmentReadyForMeasurement(appointment) {
            const clientCount = appointment.clients.length;
            if (clientCount === 0) {
                return false;
            }
            return appointment.clients[clientCount - 1].additional_info.gdpr !== undefined;
        }
    }
};
</script>

<style lang="scss" scoped>
    .c-appointment-list {
        height : 100%;
    }

    .top-actions {
        display:flex;
        align-items: center;
        justify-content: space-between;
        height: 3.7rem;
    }

    .list-container {
        max-height: 45vh;
        height: 100%;
        margin-top : 1.5em;
        display: flex;
        align-items: flex-start;

        .list {
            width: 70%;

            &--item {
                &.active {
                    border : solid 1px $color-green;
                }
            }
        }
        .detail-panel {
            width : 30%;
            margin-left : 2%;
            margin-top : 1rem;
        }
    }

    .btn-result {
        margin-right : 1rem;
    }

    .no-userdata-btn {
        position: relative;
    }

    .no-userdata-warning {
        position: absolute;
        width: auto;
        z-index: 1;
        background: $color-red;
        padding: 0.6em 1em;
        overflow: hidden;
        white-space: nowrap;
        right: -1.2rem;
        top: 2.7em;
        border-radius: 1em;
    }

    .action-btns {
        width : 100%;
        text-align: right;
        margin-top : 2rem;
    }

    :deep(.list--item.selected .bedside) {
        border : solid .05rem white;
    }

    :deep(.loading) {
        top: 0;
        height: 100vh;
    }

    //anims
    .date-picker {
        @include slideIntroAnimation(basicSlideIntro, 450ms, 450ms);
    }

    .search-form {
        @include slideIntroAnimation(basicSlideIntro, 450ms, 650ms);
    }

    .no-appointments {
        @include slideIntroAnimation(basicSlideIntro, 450ms, 650ms);
    }
</style>